/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * Stock API Service
 * Endpoints for /api/v1/venta/stock
 */

import { get, post } from '@/services/api/client';
import type { ApiResponse } from '@/services/api/types';
import type {
  StockDashboardStats,
  StockMatrix,
  StockAdjustmentRequest,
  StockAdjustmentPreview,
  StockAdjustmentResult,
  StockAdjustmentBatchRequest,
  StockAdjustmentBatchResult,
  StockMovementListItem,
  StockMovementSummary,
  StockMovementHistoryParams,
  StockByLocationItem,
  LowStockAlert,
  LowStockSummary,
  LowStockAlertParams,
  VariantStockItem,
  MigrationStatus,
  MigrateToVariantsRequest,
  MigrateToVariantsResult,
  ReclassifyStockRequest,
  ReclassifyPreviewResponse,
} from '../types/stock';
import type { PagedResult } from '../types/product';

const BASE = '/venta/stock';

export interface StockMatrixParams {
  categoryId?: string;
  search?: string;
  page?: number;
  size?: number;
}

export async function getDashboardStats(locationId?: string): Promise<StockDashboardStats> {
  const response = await get<ApiResponse<StockDashboardStats>>(`${BASE}/dashboard`, locationId ? { locationId } : undefined);
  return response.data;
}

export async function getStockMatrix(params: StockMatrixParams = {}): Promise<StockMatrix> {
  const queryParams: Record<string, unknown> = {};
  if (params.categoryId) queryParams.categoryId = params.categoryId;
  if (params.search) queryParams.search = params.search;
  if (params.page !== undefined) queryParams.page = params.page;
  if (params.size !== undefined) queryParams.size = params.size;

  const response = await get<ApiResponse<StockMatrix>>(`${BASE}/matrix`, queryParams);
  return response.data;
}

export async function getStockLevel(productId: string, locationId: string): Promise<number> {
  const response = await get<ApiResponse<Array<{ locationId: string; locationName: string; quantity: number }>>>(
    `${BASE}/levels`,
    { productId, locationId }
  );
  return response.data?.[0]?.quantity ?? 0;
}

export async function getProductStockLevels(productId: string): Promise<StockByLocationItem[]> {
  const response = await get<ApiResponse<StockByLocationItem[]>>(
    `${BASE}/levels`,
    { productId }
  );
  return response.data ?? [];
}

export interface LocationProduct {
  productId: string;
  productName: string;
  sku: string;
  quantity: number;
  hasVariants: boolean;
}

export async function getAvailableProductsAtLocation(locationId: string): Promise<LocationProduct[]> {
  const response = await get<ApiResponse<LocationProduct[]>>(`${BASE}/by-location`, { locationId });
  return response.data ?? [];
}

export async function previewAdjustment(data: StockAdjustmentRequest): Promise<StockAdjustmentPreview> {
  const response = await post<ApiResponse<StockAdjustmentPreview>>(`${BASE}/adjustments/preview`, data);
  return response.data;
}

export async function applyAdjustment(data: StockAdjustmentRequest): Promise<StockAdjustmentResult> {
  const response = await post<ApiResponse<StockAdjustmentResult>>(`${BASE}/adjustments`, data);
  return response.data;
}

export async function previewBatchAdjustment(
  data: StockAdjustmentBatchRequest,
): Promise<StockAdjustmentPreview[]> {
  const response = await post<ApiResponse<StockAdjustmentPreview[]>>(
    `${BASE}/adjustments/batch/preview`, data);
  return response.data ?? [];
}

/** Applies every line in one transaction: all land, or none do. */
export async function applyBatchAdjustment(
  data: StockAdjustmentBatchRequest,
): Promise<StockAdjustmentBatchResult> {
  const response = await post<ApiResponse<StockAdjustmentBatchResult>>(
    `${BASE}/adjustments/batch`, data);
  return response.data;
}

export async function getMovementHistory(params: StockMovementHistoryParams = {}): Promise<PagedResult<StockMovementListItem>> {
  const queryParams: Record<string, unknown> = {};
  if (params.movementType) queryParams.movementType = params.movementType;
  if (params.reason) queryParams.reason = params.reason;
  if (params.search) queryParams.search = params.search;
  if (params.page !== undefined) queryParams.page = params.page;
  if (params.size !== undefined) queryParams.size = params.size;
  if (params.sort) queryParams.sort = params.sort;
  if (params.direction) queryParams.direction = params.direction;

  const response = await get<ApiResponse<PagedResult<StockMovementListItem>>>(`${BASE}/movements`, queryParams);
  return response.data;
}

export async function getMovementSummary(): Promise<StockMovementSummary> {
  const response = await get<ApiResponse<StockMovementSummary>>(`${BASE}/movements/summary`);
  return response.data;
}

export async function getLowStockAlerts(params: LowStockAlertParams = {}): Promise<PagedResult<LowStockAlert>> {
  const queryParams: Record<string, unknown> = {};
  if (params.severity) queryParams.severity = params.severity;
  if (params.locationId) queryParams.locationId = params.locationId;
  if (params.search) queryParams.search = params.search;
  if (params.page !== undefined) queryParams.page = params.page;
  if (params.size !== undefined) queryParams.size = params.size;
  if (params.sort) queryParams.sort = params.sort;
  if (params.direction) queryParams.direction = params.direction;

  const response = await get<ApiResponse<PagedResult<LowStockAlert>>>(`${BASE}/alerts`, queryParams);
  return response.data;
}

export async function getLowStockSummary(): Promise<LowStockSummary> {
  const response = await get<ApiResponse<LowStockSummary>>(`${BASE}/alerts/summary`);
  return response.data;
}

export async function getVariantsAtLocation(productId: string, locationId: string): Promise<VariantStockItem[]> {
  const response = await get<ApiResponse<VariantStockItem[]>>(`${BASE}/variants-at-location`, { productId, locationId });
  return response.data ?? [];
}

export async function getMigrationStatus(productId: string, locationId: string): Promise<MigrationStatus> {
  const response = await get<ApiResponse<MigrationStatus>>(`${BASE}/migration-status`, { productId, locationId });
  return response.data;
}

export async function migrateToVariants(data: MigrateToVariantsRequest): Promise<MigrateToVariantsResult> {
  const response = await post<ApiResponse<MigrateToVariantsResult>>(`${BASE}/migrate-to-variants`, data);
  return response.data;
}

export async function reclassifyPreview(data: ReclassifyStockRequest): Promise<ReclassifyPreviewResponse> {
  const response = await post<ApiResponse<ReclassifyPreviewResponse>>(`${BASE}/reclassify/preview`, data);
  return response.data;
}

export async function reclassifyStock(data: ReclassifyStockRequest): Promise<void> {
  await post<ApiResponse<void>>(`${BASE}/reclassify`, data);
}

export const stockService = {
  getDashboardStats,
  getStockMatrix,
  getStockLevel,
  getProductStockLevels,
  previewAdjustment,
  applyAdjustment,
  getMovementHistory,
  getMovementSummary,
  getLowStockAlerts,
  getLowStockSummary,
};
